// useImagepaste — Phase 7 Task 7-8b: client-side glue between
//   1. file picker / drop-zone (caller fires `start(file)`)
//   2. drawing engine preview API (`engine.startImagepastePreview` / `cancel` / `lock`)
//   3. server 3-stage flow (`whiteboard.imagepaste-request` →
//      `imagepaste-token` → presigned PUT → `imagepaste-confirm` →
//      `imagepaste` broadcast).
//
// The hook consolidates several stateful concerns the React layer would
// otherwise have to juggle:
//   - pending File ref kept across the request → token round-trip (so the
//     PUT can use the same bytes the user selected).
//   - in-flight upload boolean (drives the spinner / button-disabled UX in
//     `ImagepasteOverlay`).
//   - subscription to the engine's preview state (drives the HTML overlay
//     button position + visibility).
//   - subscription to `transport.onEvent` for `imagepaste-token` /
//     `-rejected` (PUT trigger + preview cleanup).
//   - cleanup on `whiteboard.imagepaste` broadcast (engine drops preview;
//     hook drops pendingFile / inflight).
//
// (architecture.md §16 / §19.6 — frontend ↔ drawing-engine 接続点)

import type { ServerEvent } from '@oekaki/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import { imagepasteRejectText, imagepasteRetryText } from '../../application/imagepasteReject.js';
import {
  type ImagepasteFetchFn,
  type ImagepastePutOutcome,
  runImagepastePut,
} from '../../application/imagepasteUpload.js';
import type { Transport } from '../../application/ports/Transport.js';
import type { ImagepastePreviewState } from '../../infrastructure/drawing/CanvasDrawingEngine.js';
import type { ImagepasteToast } from '../layouts/classic/ImagepasteToastOverlay.js';

/**
 * The minimal surface of the canvas-engine that the imagepaste hook needs.
 * Defining it locally (instead of importing the concrete class) keeps tests
 * able to stub via plain objects without instantiating the real engine.
 *
 * `CanvasDrawingEngine` (infrastructure/drawing/CanvasDrawingEngine.ts) is
 * structurally compatible — `start()` falls back gracefully when `getCanvas`
 * is absent so a minimal stub may omit it.
 */
export interface ImagepasteEngine {
  isImagepastePreviewActive(): boolean;
  getImagepastePreviewState(): ImagepastePreviewState | null;
  onImagepastePreviewChange(cb: (s: ImagepastePreviewState | null) => void): () => void;
  startImagepastePreview(args: {
    bitmap: ImageBitmap;
    x: number;
    y: number;
    width: number;
    height: number;
    /**
     * Target layer the preview composites **directly above** (A案 WYSIWYG,
     * imagepaste×レイヤー実用化 2026-06-14). Seeded from the active drawing
     * layer at `start()`; defaults to 0 when omitted.
     */
    layerId?: number;
  }): void;
  /**
   * Move the active preview to a different target layer so its z (overlay
   * stacking) matches where the paste will land. No-op when no preview /
   * locked / unchanged. Optional so minimal test stubs may omit it.
   */
  setImagepastePreviewLayer?(layerId: number): void;
  /**
   * Resize / move the preview (送信前リサンプル方式). Optional so minimal test
   * stubs may omit it. No-op when locked / no preview.
   */
  setImagepastePreviewRect?(x: number, y: number, width: number, height: number): void;
  /**
   * PNG of the preview re-sampled at its CURRENT width x height (null = no
   * preview / encode unavailable). Optional so minimal stubs may omit it.
   */
  getImagepastePreviewBlob?(): Promise<Blob | null>;
  lockImagepastePreview(): void;
  cancelImagepastePreview(): void;
  /** Optional — used to centre the preview on the canvas at start. */
  getCanvas?(): HTMLCanvasElement;
}

export interface UseImagepasteArgs {
  /** WS transport — used to subscribe to token / rejected and to send confirm. */
  readonly transport: Transport;
  /**
   * Drawing engine. May be `null` while the canvas is not yet mounted; the
   * hook returns no-op handlers in that case so callers don't have to guard.
   */
  readonly engine: ImagepasteEngine | null;
  /**
   * Send `whiteboard.imagepaste-request`. Captured as a callback so the hook
   * doesn't import `useCases` directly (lets tests pass a spy).
   */
  readonly sendImagepasteRequest: (args: {
    contentType: 'image/png' | 'image/jpeg' | 'image/gif';
    contentLength: number;
    x: number;
    y: number;
    width: number;
    height: number;
    /**
     * Target layer the image lands on (imagepaste-layer bug A web wiring).
     * §3.1 B2: the paste-target layer is **preview-scoped state owned by this
     * hook** (`pasteLayerId`), not the app's active drawing layer. It is seeded
     * from the active layer at `start()` and only mutated by the overlay's
     * inline switch (`setPasteLayerId`) — switching the paste target never
     * touches `pen.layerId`.
     */
    layerId?: number;
  }) => void;
  /**
   * Read the currently active drawing layer id (= `chrome.pen.layerId`).
   * §3.1 B2: used **only to seed `pasteLayerId` at `start()`** so a fresh
   * preview defaults to landing on the layer the user is currently drawing on.
   * It is NOT read at confirm time and switching the paste target does not feed
   * back here. Optional so existing stubs (which omit it) keep working; when
   * absent the paste target stays undefined and the server defaults to 0.
   */
  readonly getActiveLayerId?: () => number;
  /** Send `whiteboard.imagepaste-confirm`. */
  readonly sendImagepasteConfirm: (uploadId: string) => void;
  /**
   * 再接続中は描けない (docs/_wip/reconnect-draw-lock-2026-10-12.md)。`true` を返す間は
   * `confirm()` を受け付けない (プレビューは残るので、つながったあとに確定できる)。
   */
  readonly isConnectionLocked?: () => boolean;
  /**
   * @deprecated 2026-06-14: imagepaste の拒否/失敗はチャット (system 行) ではなく
   * **キャンバス中央トースト** (`rejectToast`) に出すようになったため、本フックは
   * この callback を呼ばなくなった。呼び出し側 (useRoomChrome) は後方互換で渡し続けて
   * よいが、imagepaste 起因の system 行はもう積まれない。
   */
  readonly pushSystemLine: (text: string) => void;
  /**
   * Override `createImageBitmap` (tests). Defaults to `globalThis.createImageBitmap`.
   */
  readonly createImageBitmapFn?: (file: File) => Promise<ImageBitmap>;
  /** Override the PUT call (tests). Defaults to fetch-based impl. */
  readonly putFetchFn?: ImagepasteFetchFn;
  /**
   * Optional callback returning the currently visible canvas region in
   * **canvas coordinates** (= already divided by the display zoom). When
   * provided, `start()` centres the imagepaste preview on this region
   * instead of the full canvas centre. Falls back to canvas-centre when
   * `null` is returned or the callback is omitted.
   *
   * 2026-05-31 alpha-feedback follow-up: 4000×4000 canvas で「画面外に出てしまう」
   * 報告への対応. caller (= useRoomChrome) が現在の pan オフセット (`{tx,ty}`,
   * 2026-09-29 viewport transform 移行・決定5) + 表示枠の実寸 + zoom から算出して渡す.
   */
  readonly getViewportRect?: () => { x: number; y: number; w: number; h: number } | null;
}

/**
 * The MIME types the server (and protocol schema) accept. UI-side gate here
 * is defensive — the `<input accept="...">` already restricts it but a user
 * can drop arbitrary bytes via the OS file picker on macOS.
 */
const ACCEPTED_CONTENT_TYPES = new Set<string>(['image/png', 'image/jpeg', 'image/gif']);

export interface UseImagepasteReturn {
  /** Current engine preview state (or null when no preview). */
  readonly preview: ImagepastePreviewState | null;
  /**
   * True between confirm-click and broadcast-arrival (or between
   * confirm-click and rejected/PUT-failure). Drives the overlay's spinner
   * and disabled-button state.
   */
  readonly uploading: boolean;
  /**
   * Begin a paste from the given File. Loads the bitmap, starts the engine
   * preview at canvas-center (or the provided init position), and stores
   * the File for the eventual PUT after the token arrives.
   *
   * If a preview is already active it's replaced (cancel-then-start).
   */
  readonly start: (file: File, init?: { x?: number; y?: number }) => Promise<void>;
  /** User clicked Confirm. */
  readonly confirm: () => void;
  /**
   * Natural (original bitmap) size of the active preview; basis of the % buttons
   * and the resize limits. `null` when no preview is active.
   */
  readonly naturalSize: { readonly w: number; readonly h: number } | null;
  /** Resize / move the active preview (layer coords). No-op when locked / no preview. */
  readonly setPreviewRect: (x: number, y: number, width: number, height: number) => void;
  /** User clicked Cancel. */
  readonly cancel: () => void;
  /**
   * §3.1 B2: preview-scoped paste-target layer id. Seeded at `start()` from
   * the active drawing layer; updated by the overlay's inline switch via
   * `setPasteLayerId`. `null` when no preview is active (or no active-layer
   * resolver is wired). The overlay reflects this as the "貼り先" active chip,
   * and `confirm()` sends it as `request.layerId`.
   */
  readonly pasteLayerId: number | null;
  /**
   * §3.1 B2: change the paste-target layer (overlay chip). Does NOT touch
   * `pen.layerId` — the user's active drawing layer is unaffected.
   */
  readonly setPasteLayerId: (layerId: number) => void;
  /**
   * imagepaste 拒否トースト (= キャンバス中央表示、2026-06-14)。拒否
   * (server `imagepaste-rejected` / クライアント事前バリデーション / PUT 失敗) で
   * セットされ、`CanvasStage` の `ImagepasteToastOverlay` が描画する。`null` で非表示。
   * rate-limited は `countdown=true` で、本フックが毎秒 `text` を差し替え残り 0 で消す。
   */
  readonly rejectToast: ImagepasteToast | null;
  /**
   * 静的文言トーストの自動消滅を反映 (= ImagepasteToastOverlay の `onDismiss`)。
   * countdown トーストはフック側のタイマーが消すので、これは静的文言用。
   */
  readonly dismissRejectToast: () => void;
}

/**
 * Phase 7 Task 7-8b: full UX glue for the imagepaste flow.
 *
 * Returns the preview state + uploading flag (for overlay rendering) and
 * the start/confirm/cancel triggers (for the file picker + overlay buttons).
 */
export const useImagepaste = (args: UseImagepasteArgs): UseImagepasteReturn => {
  const {
    transport,
    engine,
    sendImagepasteRequest,
    sendImagepasteConfirm,
    // 2026-06-14: imagepaste の拒否/失敗は全てキャンバス中央トーストへ移設したため
    // hook 内では pushSystemLine を使わなくなった (= prop は後方互換で残置)。
    createImageBitmapFn,
    putFetchFn,
    getViewportRect,
    getActiveLayerId,
    isConnectionLocked,
  } = args;

  // Pending File kept across the request → token round-trip. We hold it on
  // a ref (not state) because it never affects the UI directly — the
  // overlay reads `preview` for positioning, not the file bytes.
  const isConnectionLockedRef = useRef(isConnectionLocked);
  isConnectionLockedRef.current = isConnectionLocked;
  const pendingFileRef = useRef<Blob | null>(null);
  // Natural size of the bitmap handed to the engine (state for the overlay, ref for confirm).
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const naturalSizeRef = useRef<{ w: number; h: number } | null>(null);
  // Original File + its type, kept so confirm can still send the untouched bytes
  // when the user did not resize (JPEG photos stay JPEG).
  const originalFileRef = useRef<File | null>(null);
  // Pending content-type captured at the moment of `start()` (we use
  // `file.type` but the server signs the URL with the same value, so we
  // keep it here to make the PUT bake the exact same string the request
  // declared — defensive against a future mid-flight File swap).
  const pendingContentTypeRef = useRef<'image/png' | 'image/jpeg' | 'image/gif' | null>(null);

  // Drive preview UI state from the engine. We mirror it into React state
  // because the overlay component needs to re-render on every drag move.
  const [preview, setPreview] = useState<ImagepastePreviewState | null>(null);
  const [uploading, setUploading] = useState(false);

  // §3.1 B2: preview-scoped paste-target layer id. Seeded from the active
  // drawing layer at `start()`, mutated only by the overlay chip
  // (`setPasteLayerId`). `null` between previews. Kept separate from
  // `pen.layerId` so switching the paste target never moves the user's active
  // drawing layer (the B1→B2 regression this whole change exists to fix).
  const [pasteLayerId, setPasteLayerId] = useState<number | null>(null);
  // `confirm()` is a stable useCallback; read the freshest paste-target via a
  // ref so a mid-preview chip switch is picked up without re-creating confirm.
  const pasteLayerIdRef = useRef<number | null>(null);
  pasteLayerIdRef.current = pasteLayerId;

  // ── 拒否トースト (= キャンバス中央表示、2026-06-14) ───────────────────────────
  // 拒否 (server rejected / 事前バリデーション / PUT 失敗) を `view.system` ではなく
  // キャンバス中央のトーストに出す。最新の拒否で**置換**する (= 多重トーストにしない)。
  // rate-limited は server の `retryAfterMs` から残り秒を毎秒カウントダウンし、0 で
  // 自動消滅する。タイマーは unmount / 新規拒否 / 成功 のいずれでもクリア (リーク防止)。
  const [rejectToast, setRejectToast] = useState<ImagepasteToast | null>(null);
  // トースト id を単調増加させ、auto-dismiss / カウントダウンの「同一トースト」判定に使う。
  const toastIdRef = useRef(0);
  // rate-limited のカウントダウン interval。新規拒否・成功・unmount でクリア。
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdownTimer = useCallback((): void => {
    if (countdownTimerRef.current !== null) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  // 静的文言トースト (rate-limited 以外) を出す。約 3 秒で ImagepasteToastOverlay が
  // 自動消滅させる (countdown=false)。連続拒否は最新で置換 (= 既存カウントダウンも停止)。
  const showStaticToast = useCallback(
    (text: string): void => {
      clearCountdownTimer();
      toastIdRef.current += 1;
      setRejectToast({ text, id: toastIdRef.current, countdown: false });
    },
    [clearCountdownTimer],
  );

  // rate-limited トーストを出す。`retryAfterMs` (undefined/0 ガード) から残り秒を出し、
  // 毎秒カウントダウンして 0 で消す。countdown 中は同じ id を保ち text だけ差し替える
  // (= ImagepasteToastOverlay 側で消滅時刻が伸びない)。
  const showRateLimitedToast = useCallback(
    (retryAfterMs: number | undefined): void => {
      clearCountdownTimer();
      toastIdRef.current += 1;
      const id = toastIdRef.current;
      // undefined / 0 / 負数 ガード: カウントダウンが成立しないなら静的文言にフォールバック。
      if (retryAfterMs === undefined || retryAfterMs <= 0) {
        setRejectToast({
          text: imagepasteRejectText('rate-limited'),
          id,
          countdown: false,
        });
        return;
      }
      // 残り終了時刻を固定し、毎秒 now との差から残り ms を出す (= drift しない)。
      const deadline = Date.now() + retryAfterMs;
      setRejectToast({ text: imagepasteRetryText(retryAfterMs), id, countdown: true });
      countdownTimerRef.current = setInterval(() => {
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          clearCountdownTimer();
          // 自分が出したトーストのときだけ消す (= その後に別トーストへ置換済なら触らない)。
          setRejectToast((prev) => (prev?.id === id ? null : prev));
          return;
        }
        setRejectToast((prev) =>
          prev?.id === id ? { ...prev, text: imagepasteRetryText(remaining) } : prev,
        );
      }, 1_000);
    },
    [clearCountdownTimer],
  );

  // 静的文言トーストの自動消滅 (ImagepasteToastOverlay の onDismiss)。countdown 中の
  // トーストを誤って消さないよう、countdown=false のときだけ null にする。
  const dismissRejectToast = useCallback((): void => {
    setRejectToast((prev) => (prev !== null && !prev.countdown ? null : prev));
  }, []);

  // unmount でカウントダウン interval を必ずクリア (リーク防止)。
  useEffect(() => clearCountdownTimer, [clearCountdownTimer]);

  // Subscribe to the engine's preview-state channel. Re-subscribe when the
  // engine reference changes (mount / unmount / dispose).
  useEffect(() => {
    if (engine === null) {
      setPreview(null);
      return;
    }
    setPreview(engine.getImagepastePreviewState());
    const unsub = engine.onImagepastePreviewChange((state) => {
      setPreview(state);
      // Engine drops to null on either commit (broadcast arrived) or
      // explicit cancel / failure. Either way clear our pending bookkeeping.
      if (state === null) {
        pendingFileRef.current = null;
        pendingContentTypeRef.current = null;
        originalFileRef.current = null;
        naturalSizeRef.current = null;
        setNaturalSize(null);
        setUploading(false);
        // §3.1 B2: preview ended (commit / cancel / failure) → drop the
        // paste-target so the next `start()` re-seeds from the active layer.
        setPasteLayerId(null);
      }
    });
    return unsub;
  }, [engine]);

  // Listen for token / rejected events on the transport. Token triggers the
  // PUT + confirm; rejected drops the local preview (the reducer already
  // pushed a localized system line for us, see RoomSession.ts).
  useEffect(() => {
    if (engine === null) return;
    const unsub = transport.onEvent((event: ServerEvent) => {
      if (event.t === 'whiteboard.imagepaste-token') {
        const file = pendingFileRef.current;
        const contentType = pendingContentTypeRef.current;
        if (file === null || contentType === null) {
          // No pending paste — likely the user cancelled between request
          // and token. Nothing to do; the server-side TTL will reap the
          // upload slot eventually.
          return;
        }
        void runConfirmFlow({
          token: event,
          file,
          contentType,
          engine,
          sendImagepasteConfirm,
          // 2026-06-14: PUT 失敗もキャンバス中央トーストへ (= 貼付文脈の一貫性)。
          onError: showStaticToast,
          putFetchFn,
        });
        return;
      }
      if (event.t === 'whiteboard.imagepaste') {
        // 成功 broadcast: 念のため拒否トースト (= 残っていれば古い静的文言 /
        // 進行中のカウントダウン) を畳む (リーク防止 + UI ノイズ除去)。プレビュー
        // commit は engine が別途処理する (= ここでは触らない)。
        clearCountdownTimer();
        setRejectToast(null);
        return;
      }
      if (event.t === 'whiteboard.imagepaste-rejected') {
        // 2026-06-14: 拒否はチャット (view.system) ではなく**キャンバス中央トースト**へ。
        // RoomSession の reducer は view.system に積まなくなった (= 移設の本体)。ここで
        // トースト state を立てる。rate-limited は server の retryAfterMs から毎秒
        // カウントダウン、その他 reason は静的文言を約 3 秒で自動消滅させる。
        if (event.reason === 'rate-limited') {
          showRateLimitedToast(event.retryAfterMs);
        } else {
          showStaticToast(imagepasteRejectText(event.reason, event.message));
        }
        // ローカルプレビューも畳む (= overlay を消す + pending bookkeeping クリア。
        // engine listener が null 受信時にも片付ける)。
        if (engine.isImagepastePreviewActive()) {
          engine.cancelImagepastePreview();
        }
        return;
      }
    });
    return unsub;
  }, [
    engine,
    transport,
    sendImagepasteConfirm,
    putFetchFn,
    showRateLimitedToast,
    showStaticToast,
    clearCountdownTimer,
  ]);

  const start = useCallback<UseImagepasteReturn['start']>(
    async (file, init) => {
      if (engine === null) return;
      // Validate MIME type up front (defensive — `<input accept>` doesn't
      // bind on every OS file picker). 2026-06-14: 貼付中の文脈で一貫させるため
      // クライアント事前バリデーション拒否も**キャンバス中央トースト**へ寄せる
      // (= 旧 pushSystemLine 廃止)。文言は server 'invalid-content-type' と共有。
      if (!ACCEPTED_CONTENT_TYPES.has(file.type)) {
        showStaticToast(imagepasteRejectText('invalid-content-type'));
        return;
      }
      // If a preview is already active, cancel it first (single-instance UX).
      if (engine.isImagepastePreviewActive()) {
        engine.cancelImagepastePreview();
      }
      const decoder = createImageBitmapFn ?? defaultCreateImageBitmap;
      let bitmap: ImageBitmap;
      try {
        bitmap = await decoder(file);
      } catch (cause) {
        // eslint-disable-next-line no-console
        console.warn('[useImagepaste] createImageBitmap failed', cause);
        // 2026-06-14: 読み込み失敗もキャンバス中央トーストへ (貼付文脈の一貫性)。
        showStaticToast('画像の読み込みに失敗しました');
        return;
      }
      // Default initial position: centre of the currently visible viewport
      // (= getViewportRect の戻り値が中央点). Falls back to canvas-centre when
      // viewport rect is unavailable. 4000×4000 canvas で画面外に貼られる
      // 問題への対応 (2026-05-31 alpha-feedback follow-up).
      //
      // If `getCanvas` is unavailable (test stub), fall back to the bitmap's
      // own dims so the initial (x, y) is (0, 0).
      const canvas = engine.getCanvas?.();
      const canvasW = canvas?.width ?? bitmap.width;
      const canvasH = canvas?.height ?? bitmap.height;
      const viewportRect = getViewportRect?.() ?? null;
      const centreX = viewportRect !== null ? viewportRect.x + viewportRect.w / 2 : canvasW / 2;
      const centreY = viewportRect !== null ? viewportRect.y + viewportRect.h / 2 : canvasH / 2;
      // 中心点から bitmap 半分を引いて top-left を出し、canvas 範囲に clamp.
      // viewport が小さい / canvas からはみ出てる場合でも (0, 0) を下回らない.
      const defaultX = Math.max(
        0,
        Math.min(Math.max(0, canvasW - bitmap.width), Math.floor(centreX - bitmap.width / 2)),
      );
      const defaultY = Math.max(
        0,
        Math.min(Math.max(0, canvasH - bitmap.height), Math.floor(centreY - bitmap.height / 2)),
      );
      const x = init?.x ?? defaultX;
      const y = init?.y ?? defaultY;
      pendingFileRef.current = file;
      originalFileRef.current = file;
      pendingContentTypeRef.current = file.type as 'image/png' | 'image/jpeg' | 'image/gif';
      naturalSizeRef.current = { w: bitmap.width, h: bitmap.height };
      setNaturalSize({ w: bitmap.width, h: bitmap.height });
      // §3.1 B2: seed the paste-target with the layer the user is currently
      // drawing on (read once, here). From now on only the overlay chip moves
      // it — `pen.layerId` is never consulted again for this preview.
      // A案 WYSIWYG (2026-06-14): resolve the seed first so we can hand the
      // **same value** to the engine, making the preview composite directly
      // above the paste-target layer from the very start (not z=0).
      const seedLayerId = getActiveLayerId?.() ?? null;
      setPasteLayerId(seedLayerId);
      engine.startImagepastePreview({
        bitmap,
        x,
        y,
        width: bitmap.width,
        height: bitmap.height,
        // null seed → omit so the engine falls back to 0 (= old behaviour).
        ...(seedLayerId !== null ? { layerId: seedLayerId } : {}),
      });
    },
    [engine, createImageBitmapFn, showStaticToast, getViewportRect, getActiveLayerId],
  );

  const confirm = useCallback<UseImagepasteReturn['confirm']>(() => {
    if (engine === null) return;
    if (isConnectionLockedRef.current?.() === true) return;
    const before = engine.getImagepastePreviewState();
    const file = pendingFileRef.current;
    const contentType = pendingContentTypeRef.current;
    if (before === null || file === null || contentType === null) return;
    if (before.locked) return; // double-click guard
    engine.lockImagepastePreview();
    setUploading(true);
    // Lock first, THEN read the state: a locked preview can no longer be resized, so
    // the width/height we put in the request are exactly what the Blob is sampled at.
    const state = engine.getImagepastePreviewState() ?? before;
    // §3.1 B2: send the **preview-scoped paste-target** (= what the overlay
    // chip last selected, or the active layer at start time). NOT `pen.layerId`.
    // Omit when null (no resolver wired) so the server falls back to `.default(0)`.
    const layerId = pasteLayerIdRef.current;
    const send = (type: 'image/png' | 'image/jpeg' | 'image/gif', size: number): void => {
      sendImagepasteRequest({
        contentType: type,
        contentLength: size,
        x: state.x,
        y: state.y,
        width: state.width,
        height: state.height,
        ...(layerId !== null ? { layerId } : {}),
      });
    };

    const natural = naturalSizeRef.current;
    const original = originalFileRef.current;
    const unresized = natural !== null && state.width === natural.w && state.height === natural.h;
    if (unresized && original !== null && contentType !== 'image/gif') {
      // Unchanged PNG/JPEG: upload the original bytes (a JPEG photo re-encoded as PNG
      // would be needlessly large and could hit the 5MB cap).
      send(contentType, file.size);
      return;
    }

    // Resized (or GIF → first frame): upload the engine's re-sampled PNG.
    void (async () => {
      let blob: Blob | null = null;
      try {
        blob = (await engine.getImagepastePreviewBlob?.()) ?? null;
      } catch (cause) {
        // eslint-disable-next-line no-console
        console.warn('[useImagepaste] getImagepastePreviewBlob failed', cause);
      }
      // Cancelled (or replaced) while encoding → nothing to send.
      if (!engine.isImagepastePreviewActive()) return;
      if (blob === null) {
        engine.cancelImagepastePreview();
        showStaticToast('画像の変換に失敗しました');
        return;
      }
      pendingFileRef.current = blob;
      pendingContentTypeRef.current = 'image/png';
      send('image/png', blob.size);
    })();
  }, [engine, sendImagepasteRequest, showStaticToast]);

  const setPreviewRect = useCallback<UseImagepasteReturn['setPreviewRect']>(
    (x, y, width, height) => {
      engine?.setImagepastePreviewRect?.(x, y, width, height);
    },
    [engine],
  );

  const cancel = useCallback<UseImagepasteReturn['cancel']>(() => {
    if (engine === null) return;
    engine.cancelImagepastePreview();
    // pendingFileRef / uploading は engine-listener が null 受信時にクリアする
  }, [engine]);

  // §3.1 B2 + A案 WYSIWYG (2026-06-14): overlay chip switch. Updates the
  // preview-scoped paste-target React state (drives the "貼り先" active chip
  // + `confirm()`'s request.layerId) AND moves the engine overlay's z so the
  // preview composites directly above the newly-selected layer in real time.
  // `pen.layerId` (the user's active drawing layer) is never touched.
  // `setImagepastePreviewLayer` is a no-op when locked (PUT in flight) /
  // no preview / unchanged, so it's safe to call unconditionally.
  const handleSetPasteLayer = useCallback<UseImagepasteReturn['setPasteLayerId']>(
    (layerId) => {
      setPasteLayerId(layerId);
      engine?.setImagepastePreviewLayer?.(layerId);
    },
    [engine],
  );

  return {
    preview,
    uploading,
    start,
    confirm,
    naturalSize,
    setPreviewRect,
    cancel,
    pasteLayerId,
    setPasteLayerId: handleSetPasteLayer,
    rejectToast,
    dismissRejectToast,
  };
};

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * The actual PUT-then-confirm chain. Extracted so the token listener stays
 * trivially short. Caller has already validated `file` / `contentType` /
 * `engine.isImagepastePreviewActive()`.
 */
const runConfirmFlow = async (args: {
  token: { uploadId: string; putUrl: string };
  file: Blob;
  contentType: 'image/png' | 'image/jpeg' | 'image/gif';
  engine: ImagepasteEngine;
  sendImagepasteConfirm: (uploadId: string) => void;
  /**
   * 2026-06-14: PUT 失敗時のユーザ向け文言を表示する (= 旧 pushSystemLine 廃止、
   * キャンバス中央トーストへ移設)。呼び出し側は `showStaticToast` を渡す。
   */
  onError: (text: string) => void;
  putFetchFn?: ImagepasteFetchFn;
}): Promise<void> => {
  const { token, file, contentType, engine, sendImagepasteConfirm, onError, putFetchFn } = args;
  let outcome: ImagepastePutOutcome;
  try {
    outcome = await runImagepastePut({
      putUrl: token.putUrl,
      file,
      contentType,
      ...(putFetchFn !== undefined ? { fetchFn: putFetchFn } : {}),
    });
  } catch (cause) {
    // runImagepastePut itself never throws (it converts to put-failed),
    // but defend anyway so an exotic injection couldn't deadlock the UI.
    // eslint-disable-next-line no-console
    console.warn('[useImagepaste] runImagepastePut threw', cause);
    if (engine.isImagepastePreviewActive()) {
      engine.cancelImagepastePreview();
    }
    onError('画像のアップロードに失敗しました');
    return;
  }
  if (outcome.outcome === 'ok') {
    // Hand off to server: it will HEAD the object then either broadcast
    // `whiteboard.imagepaste` (engine commits + drops preview) or reply
    // `imagepaste-rejected` (rejected listener drops preview).
    sendImagepasteConfirm(token.uploadId);
    return;
  }
  // PUT failed. Drop the preview locally + surface a toast.
  if (engine.isImagepastePreviewActive()) {
    engine.cancelImagepastePreview();
  }
  onError(outcome.message);
};

const defaultCreateImageBitmap = (file: File): Promise<ImageBitmap> => {
  const f = globalThis.createImageBitmap as ((b: Blob) => Promise<ImageBitmap>) | undefined;
  if (f === undefined) {
    return Promise.reject(new Error('globalThis.createImageBitmap is not available'));
  }
  return f.call(globalThis, file);
};
